import classNames from 'classnames';
import React, { useMemo } from 'react';
import {
  makeStyles,
  Link,
  Typography,
} from '~/lib/bn-material-ui';
import { useSirenEntity, useStore, useAccountFeatures, muiTheme } from '~/utility/BinSentry-ui-utility';
import { OrganizationPathName, OrganizationPathNameLinks, PathNameClasses, PathNameChevron } from '~/organizations/organization-path-name';
import { useOrganizationAncestors } from '~/organizations/organization-ancestors';
import { OrganizationType, OrganizationTypeEntity, ALL_ORGANIZATION_TYPES } from '~/types/organization-type-entity';
import { OrganizationEntity } from '~/types/organization-entity';

const usePathNameStyles = makeStyles<typeof muiTheme>(({ breakpoints }) => {
  return {
    pathItem: {
      display: 'flex',
      whiteSpace: 'nowrap',
      alignItems: 'center',
      '& p': {
        maxWidth: '20rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        [breakpoints.down('sm')]: {
          maxWidth: '7rem',
        },
      },
    },
    link: {
      padding: 0,
      minWidth: 'unset',
      cursor: 'pointer',
    },
  };
});

function useOrganizationLinks({
  binId,
  endOrganizationType,
} : {
  binId?: string,
  endOrganizationType: OrganizationType,
}): OrganizationPathNameLinks {
  const { isProducer, userFeatures } = useStore();
  const { entity: binEntity } = useSirenEntity({ href: binId });
  const organizationId = binEntity?.getSubEntityByRel('https://api.binsentry.com/rel/bin-owner').href;
  const { features } = useAccountFeatures({ organizationId });

  const { ancestors = [] }: { ancestors?: OrganizationEntity[] } = useOrganizationAncestors({
    startType: 'barn',
    endType: endOrganizationType,
    href: organizationId,
  });

  return useMemo(() => Object.fromEntries(ancestors.map((ancestor: OrganizationEntity) => {
    const legacyBinListEnabled = isProducer && (features?.legacyBinList || userFeatures?.legacyBinList);
    const organizationTypeEntity = ancestor.getSubEntity('https://api.binsentry.com/rel/organization-type') as OrganizationTypeEntity;
    const organizationType = organizationTypeEntity?.properties.name;
    const organizationId = ancestor.getLinkByRel('self')?.href as string;
    if (binEntity?.properties.role === 'ingredient') {
      return [organizationType, `/inventory?organizationId=${encodeURIComponent(organizationId)}`];
    } else if (legacyBinListEnabled) {
      return [organizationType, `/bins/list?orgId=${encodeURIComponent(organizationId)}`];
    } else {
      return [organizationType, `/customers?organizationId=${encodeURIComponent(organizationId)}`];
    }
  }))
  , [ancestors, binEntity, features, isProducer, userFeatures]);
}

export function BinPathName({
  binHref,
  endOrganizationType = 'account',
  openNewWindow = false,
  organizationTypeLinks = {},
  showOrganizationTypeLinks = ALL_ORGANIZATION_TYPES,
  binLink,
  showBinLink = false,
  classes,
}: {
  binHref?: string;
  endOrganizationType?: OrganizationType;
  openNewWindow?: boolean;
  organizationTypeLinks?: OrganizationPathNameLinks;
  showOrganizationTypeLinks?: OrganizationType[];
  binLink?: string;
  showBinLink?: boolean;
  classes?: PathNameClasses;
}) {
  const styles = usePathNameStyles();
  const { entity: binEntity, loading: loadingBin } = useSirenEntity({ href: binHref });
  const organizationId = binEntity?.getSubEntityByRel('https://api.binsentry.com/rel/bin-owner').href;
  const defaultOrganizationTypeLinks = useOrganizationLinks({ binId: binHref, endOrganizationType });

  if (!binHref || !binEntity || loadingBin) {
    return null;
  }

  const href = binLink || `/bins/show/overview?binId=${encodeURIComponent(binHref)}`;

  return <OrganizationPathName
    organizationHref={organizationId}
    endOrganizationType={endOrganizationType}
    openNewWindow={openNewWindow}
    organizationTypeLinks={{ ...defaultOrganizationTypeLinks, ...organizationTypeLinks }}
    showOrganizationTypeLinks={showOrganizationTypeLinks}
    classes={classes}
  >
    <PathNameChevron className={classes?.chevron} />
    {showBinLink && href
      ? <Link
        href={href}
        {...openNewWindow ? { target: '_blank' } : {}}
        className={classNames(styles.link, classes?.link)}
      ><Typography>{binEntity.properties.name}</Typography></Link>
      : <Typography>{binEntity.properties.name}</Typography>
    }
  </OrganizationPathName>;
}
